import React, {useState, useEffect, useContext} from 'react';
import axios from 'axios';
import { UserContext } from '../Context/UserContext';
import {
    Box,
    Paper,
    Table,
    TableBody,
    TableCell,
    TableContainer,
    TableHead,
    TableRow,
    TablePagination,
    Typography,
    IconButton,
    Chip,
    TextField,
    InputAdornment,
    Grid,
    Card,
    CardContent,
    FormControl,
    InputLabel,
    Select,
    MenuItem,
    CircularProgress,
    Alert,
    Button,
    Dialog,
    DialogTitle,
    DialogContent,
    DialogActions,
    ToggleButton,
    ToggleButtonGroup,
    Stack,
    Tooltip,
} from '@mui/material';
import {
    Search as SearchIcon,
    Email as EmailIcon,
    Business as BusinessIcon,
    Settings as SettingsIcon,
    Person as PersonIcon, Person, Business,
    Delete as DeleteIcon,
    Edit as EditIcon,
} from '@mui/icons-material';

import {useNavigate} from 'react-router';
import PaysSelect from '../Componant/PaysSelect';
import AdresseForm from '../Componant/AdresseForm';

const TYPE_MAP = {individual: 'Particulier', company: 'Entreprise'};

const API_URL = process.env.REACT_APP_BACKEND_API_URL || 'http://localhost:8080';

export default function ClientsPage() {
    const navigate = useNavigate();
    const { userInfo } = useContext(UserContext);
    const canDelete = userInfo?.is_super_admin === true || (userInfo?.permissions || []).includes('clients.delete');
    const [clients, setClients] = useState([]);
    const [loading, setLoading] = useState(true);
    const [error, setError] = useState(null);
    const [page, setPage] = useState(0);
    const [rowsPerPage, setRowsPerPage] = useState(10);
    const [searchTerm, setSearchTerm] = useState('');
    const [typeFilter, setTypeFilter] = useState('all');
    const [openCreateClient, setOpenCreateClient] = useState(false);
    const [clientType, setClientType] = useState('particulier');
    const [creating, setCreating] = useState(false);
    const [createError, setCreateError] = useState(null);
    const [openEdit, setOpenEdit] = useState(false);
    const [editingClient, setEditingClient] = useState(null);
    const [editForm, setEditForm] = useState({ nom: '', raison_sociale: '', type_client: 'individual' });
    const [editing, setEditing] = useState(false);
    const [editError, setEditError] = useState(null);
    const [form, setForm] = useState({
        raison_sociale: '', siret: '',
        nom: '', prenom: '', email: '', phone: '', phone_mobile: '',
        rue: '', ville: '', code_postal: '', pays: 'France',
    });

    const setF = (key) => (e) => setForm(f => ({ ...f, [key]: e.target.value }));

    const handleChange = (event, newType) => {
        if (newType !== null) setClientType(newType);
    };

    const isFormValid = () => {
        if (!form.nom.trim() || !form.prenom.trim() || !form.email.trim()) return false;
        if (clientType === 'entreprise' && !form.raison_sociale.trim()) return false;
        if (!form.rue.trim() || !form.ville.trim() || !form.code_postal.trim()) return false;
        return true;
    };

    const handleCreateClient = async () => {
        setCreating(true);
        setCreateError(null);
        const token = document.cookie.match(/AUTOMONE_ACCESS=([^;]+)/)?.[1];
        try {
            const payload = {
                type_client: clientType === 'entreprise' ? 'company' : 'individual',
                nom: clientType === 'entreprise' ? form.raison_sociale : form.nom,
                raison_sociale: clientType === 'entreprise' ? form.raison_sociale : null,
                utilisateurs: [{
                    nom: form.nom,
                    prenom: form.prenom,
                    email: form.email,
                    phone: form.phone || form.phone_mobile || null,
                    is_administrator_account: true,
                }],
                adresses: [{
                    rue: form.rue,
                    ville: form.ville,
                    code_postal: form.code_postal,
                    pays: form.pays,
                }],
            };
            await axios.post(`${API_URL}/api/clients`, payload, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setOpenCreateClient(false);
            setForm({ raison_sociale: '', siret: '', nom: '', prenom: '', email: '', phone: '', phone_mobile: '', rue: '', ville: '', code_postal: '', pays: 'France' });
            // Reload list
            const res = await axios.get(`${API_URL}/api/clients`, { headers: { Authorization: `Bearer ${token}` } });
            const mapped = (res.data || []).map(c => ({
                id: c.id_client,
                name: c.type_client === 'company' ? (c.raison_sociale || c.nom || '—') : (c.nom || c.raison_sociale || '—'),
                contact: c.Utilisateurs?.[0] ? `${c.Utilisateurs[0].prenom || ''} ${c.Utilisateurs[0].nom || ''}`.trim() || '—' : '—',
                email: c.Utilisateurs?.[0]?.email || '—',
                phone: c.Utilisateurs?.[0]?.phone || '—',
                type: TYPE_MAP[c.type_client] || c.type_client || '—',
                createdAt: c.date_creation ? new Date(c.date_creation).toLocaleDateString('fr-FR') : '—',
                nbUsers: c.Utilisateurs?.length ?? 0,
                _raw: c,
            }));
            setClients(mapped);
        } catch (err) {
            setCreateError(err.response?.data?.error || err.message || 'Erreur lors de la création');
        } finally {
            setCreating(false);
        }
    };
    // ── Load real clients from API ─────────────────────────────────────────────
    useEffect(() => {
        const token = document.cookie.match(/AUTOMONE_ACCESS=([^;]+)/)?.[1];
        if (!token) {
            setLoading(false);
            return;
        }

        axios.get(`${API_URL}/api/clients`, {headers: {Authorization: `Bearer ${token}`}})
            .then(res => {
                const mapped = (res.data || []).map(c => ({
                    id: c.id_client,
                    name: c.type_client === 'company'
                        ? (c.raison_sociale || c.nom || '—')
                        : (c.nom || c.raison_sociale || '—'),
                    contact: c.Utilisateurs?.[0]
                        ? `${c.Utilisateurs[0].prenom || ''} ${c.Utilisateurs[0].nom || ''}`.trim() || '—'
                        : '—',
                    email: c.Utilisateurs?.[0]?.email || '—',
                    phone: c.Utilisateurs?.[0]?.phone || '—',
                    type: TYPE_MAP[c.type_client] || c.type_client || '—',
                    createdAt: c.date_creation
                        ? new Date(c.date_creation).toLocaleDateString('fr-FR')
                        : '—',
                    nbUsers: c.Utilisateurs?.length ?? 0,
                    _raw: c,
                }));
                setClients(mapped);
            })
            .catch(err => {
                setError('Impossible de charger les clients');
                console.error(err.message);
            })
            .finally(() => setLoading(false));
    }, []);

    const filteredClients = clients.filter(client => {
        const matchesSearch = client.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.contact.toLowerCase().includes(searchTerm.toLowerCase()) ||
            client.email.toLowerCase().includes(searchTerm.toLowerCase());
        const matchesType = typeFilter === 'all' || client.type === typeFilter;
        return matchesSearch && matchesType;
    });

    const handleChangePage = (event, newPage) => {
        setPage(newPage);
    };

    const handleChangeRowsPerPage = (event) => {
        setRowsPerPage(parseInt(event.target.value, 10));
        setPage(0);
    };
    const handleCloseDialogCreateClient = () => { setOpenCreateClient(false); };

    const handleOpenEdit = (client) => {
        setEditingClient(client);
        setEditForm({
            nom: client._raw.nom || '',
            raison_sociale: client._raw.raison_sociale || '',
            type_client: client._raw.type_client || 'individual',
        });
        setEditError(null);
        setOpenEdit(true);
    };

    const handleEdit = async () => {
        setEditing(true); setEditError(null);
        const token = document.cookie.match(/AUTOMONE_ACCESS=([^;]+)/)?.[1];
        try {
            await axios.put(`${API_URL}/api/clients/${editingClient.id}`, editForm, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setOpenEdit(false);
            const res = await axios.get(`${API_URL}/api/clients`, { headers: { Authorization: `Bearer ${token}` } });
            setClients((res.data || []).map(c => ({
                id: c.id_client,
                name: c.type_client === 'company' ? (c.raison_sociale || c.nom || '—') : (c.nom || c.raison_sociale || '—'),
                contact: c.Utilisateurs?.[0] ? `${c.Utilisateurs[0].prenom || ''} ${c.Utilisateurs[0].nom || ''}`.trim() || '—' : '—',
                email: c.Utilisateurs?.[0]?.email || '—',
                phone: c.Utilisateurs?.[0]?.phone || '—',
                type: TYPE_MAP[c.type_client] || c.type_client || '—',
                createdAt: c.date_creation ? new Date(c.date_creation).toLocaleDateString('fr-FR') : '—',
                nbUsers: c.Utilisateurs?.length ?? 0,
                _raw: c,
            })));
        } catch (e) {
            setEditError(e.response?.data?.error || e.message);
        } finally {
            setEditing(false);
        }
    };

    const handleDelete = async (client) => {
        if (!window.confirm(`Supprimer le client "${client.name}" ? Cette action est irréversible.`)) return;
        const token = document.cookie.match(/AUTOMONE_ACCESS=([^;]+)/)?.[1];
        try {
            await axios.delete(`${API_URL}/api/clients/${client.id}`, {
                headers: { Authorization: `Bearer ${token}` },
            });
            setClients(prev => prev.filter(c => c.id !== client.id));
        } catch (e) {
            setError(e.response?.data?.error || 'Erreur lors de la suppression');
        }
    };


    const stats = {
        total: clients.length,
        totalEntreprises: clients.filter(c => c.type === 'Entreprise').length,
        totalParticuliers: clients.filter(c => c.type === 'Particulier').length,
    };

    return (
        <Box sx={{p: {xs: 2, sm: 3}}}>
            <Typography variant="h4" gutterBottom sx={{color: 'primary.main', fontWeight: 'bold'}}>
                Gestion des Clients
            </Typography>

            {error && <Alert severity="error" sx={{mb: 2}}>{error}</Alert>}

            {/* Stats Cards */}
            <Grid container spacing={3} sx={{mb: 3}}>
                {[
                    {label: 'Total', value: stats.total, color: 'primary.main'},
                    {label: 'Entreprises', value: stats.totalEntreprises, color: 'success.main'},
                    {label: 'Particuliers', value: stats.totalParticuliers, color: 'info.main'},
                ].map(s => (
                    <Grid item xs={12} sm={4} key={s.label}>
                        <Card>
                            <CardContent>
                                <Typography color="textSecondary" gutterBottom>{s.label}</Typography>
                                <Typography variant="h4" sx={{color: s.color}}>
                                    {loading ? '…' : s.value}
                                </Typography>
                            </CardContent>
                        </Card>
                    </Grid>
                ))}
            </Grid>

            {/* Search and Filters */}
            <Box sx={{mb: 3, display: 'flex', flexDirection: {xs: 'column', sm: 'row'}, gap: 2, alignItems: {xs: 'stretch', sm: 'center'}}}>
                <TextField
                    placeholder="Rechercher un client..."
                    value={searchTerm}
                    onChange={(e) => setSearchTerm(e.target.value)}
                    InputProps={{
                        startAdornment: (
                            <InputAdornment position="start">
                                <SearchIcon/>
                            </InputAdornment>
                        ),
                    }}
                    sx={{flexGrow: 1}}
                />
                <FormControl sx={{minWidth: {xs: '100%', sm: 140}}}>
                    <InputLabel>Type</InputLabel>
                    <Select
                        value={typeFilter}
                        onChange={(e) => setTypeFilter(e.target.value)}
                        label="Type"
                    >
                        <MenuItem value="all">Tous</MenuItem>
                        <MenuItem value="Entreprise">Entreprises</MenuItem>
                        <MenuItem value="Particulier">Particuliers</MenuItem>
                    </Select>
                </FormControl>
                <Button variant={'contained'} onClick={() => setOpenCreateClient(true)}>Ajouter</Button>
            </Box>

            {/* Table */}
            <Paper sx={{overflowX: 'hidden', width: '100%'}}>
                <Box sx={{overflowX: 'auto', width: '100%'}}>
                    <TableContainer>
                        <Table size="small" sx={{minWidth: {xs: '100%', sm: '650px'}}}>
                            <TableHead>
                                <TableRow>
                                    <TableCell>Nom</TableCell>
                                    <TableCell>Contact principal</TableCell>
                                    <TableCell>Email</TableCell>
                                    <TableCell>Type</TableCell>
                                    <TableCell>Utilisateurs</TableCell>
                                    <TableCell>Créé le</TableCell>
                                    <TableCell>Actions</TableCell>
                                </TableRow>
                            </TableHead>
                            <TableBody>
                                {loading ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center" sx={{py: 3}}>
                                            <CircularProgress size={24}/>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredClients.length === 0 ? (
                                    <TableRow>
                                        <TableCell colSpan={7} align="center">
                                            <Typography color="text.secondary">Aucun client trouvé</Typography>
                                        </TableCell>
                                    </TableRow>
                                ) : filteredClients
                                    .slice(page * rowsPerPage, page * rowsPerPage + rowsPerPage)
                                    .map((client) => (
                                        <TableRow key={client.id} hover>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <BusinessIcon sx={{mr: 1, color: 'primary.main', fontSize: 18}}/>
                                                    <Typography variant="body2"
                                                                fontWeight={500}>{client.name}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2">{client.contact}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Box sx={{display: 'flex', alignItems: 'center'}}>
                                                    <EmailIcon sx={{mr: 0.5, fontSize: 14, color: 'text.secondary'}}/>
                                                    <Typography variant="body2"
                                                                fontSize={12}>{client.email}</Typography>
                                                </Box>
                                            </TableCell>
                                            <TableCell>
                                                <Chip
                                                    label={client.type}
                                                    color={client.type === 'Entreprise' ? 'success' : 'info'}
                                                    size="small"
                                                />
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2"
                                                            fontWeight={600}>{client.nbUsers}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <Typography variant="body2"
                                                            color="text.secondary">{client.createdAt}</Typography>
                                            </TableCell>
                                            <TableCell>
                                                <IconButton
                                                    onClick={() => navigate(`/clients/${client.id}`)}
                                                    size="small"
                                                    sx={{color: 'primary.main'}}
                                                    title="Gérer ce client"
                                                >
                                                    <SettingsIcon/>
                                                </IconButton>
                                                <IconButton
                                                    onClick={() => handleOpenEdit(client)}
                                                    size="small"
                                                    title="Modifier"
                                                >
                                                    <EditIcon fontSize="small"/>
                                                </IconButton>
                                                {canDelete && (
                                                    <IconButton
                                                        onClick={() => handleDelete(client)}
                                                        size="small"
                                                        sx={{color: 'error.main'}}
                                                        title="Supprimer"
                                                    >
                                                        <DeleteIcon fontSize="small"/>
                                                    </IconButton>
                                                )}
                                            </TableCell>
                                        </TableRow>
                                    ))}
                            </TableBody>
                        </Table>
                    </TableContainer>
                </Box>
                <TablePagination
                    rowsPerPageOptions={[5, 10, 25]}
                    component="div"
                    count={filteredClients.length}
                    rowsPerPage={rowsPerPage}
                    page={page}
                    onPageChange={handleChangePage}
                    onRowsPerPageChange={handleChangeRowsPerPage}
                />
            </Paper>
            {/* Dialog Edit client */}
            <Dialog open={openEdit} onClose={() => setOpenEdit(false)} maxWidth="sm" fullWidth>
                <DialogTitle>Modifier le client</DialogTitle>
                <DialogContent>
                    <Stack spacing={2} sx={{ mt: 1 }}>
                        {editError && <Alert severity="error">{editError}</Alert>}
                        <FormControl fullWidth>
                            <InputLabel>Type</InputLabel>
                            <Select value={editForm.type_client} label="Type"
                                onChange={e => setEditForm(f => ({ ...f, type_client: e.target.value }))}>
                                <MenuItem value="individual">Particulier</MenuItem>
                                <MenuItem value="company">Entreprise</MenuItem>
                            </Select>
                        </FormControl>
                        <TextField fullWidth label="Nom" value={editForm.nom}
                            onChange={e => setEditForm(f => ({ ...f, nom: e.target.value }))} />
                        {editForm.type_client === 'company' && (
                            <TextField fullWidth label="Raison sociale" value={editForm.raison_sociale}
                                onChange={e => setEditForm(f => ({ ...f, raison_sociale: e.target.value }))} />
                        )}
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={() => setOpenEdit(false)}>Annuler</Button>
                    <Button variant="contained" onClick={handleEdit} disabled={editing}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}>
                        {editing ? <CircularProgress size={20} color="inherit" /> : 'Enregistrer'}
                    </Button>
                </DialogActions>
            </Dialog>

            <Dialog
                open={openCreateClient}
                onClose={handleCloseDialogCreateClient}
                maxWidth={'lg'}
                fullWidth
            >
                <DialogTitle>Création d'un nouveau client</DialogTitle>
                <DialogContent>
                    <Stack direction={'column'} spacing={3} sx={{ mt: 1 }}>

                        {createError && <Alert severity="error">{createError}</Alert>}

                        {/* Type */}
                        <Box>
                            <Typography variant="h6" gutterBottom>Type de client</Typography>
                            <ToggleButtonGroup value={clientType} exclusive onChange={handleChange} color="primary">
                                <ToggleButton value="particulier"><Person sx={{mr: 1}}/>Particulier</ToggleButton>
                                <ToggleButton value="entreprise"><Business sx={{mr: 1}}/>Entreprise</ToggleButton>
                            </ToggleButtonGroup>
                        </Box>

                        <Grid container spacing={3}>
                            {/* Bloc entreprise */}
                            {clientType === 'entreprise' && (
                                <Grid size={{ xs: 12, md: 6 }}>
                                    <Typography variant="h6" gutterBottom>Informations entreprise</Typography>
                                    <Stack spacing={2}>
                                        <TextField required label="Raison sociale" fullWidth value={form.raison_sociale} onChange={setF('raison_sociale')} />
                                        <TextField label="SIRET" fullWidth value={form.siret} onChange={setF('siret')} />
                                    </Stack>
                                </Grid>
                            )}

                            {/* Bloc utilisateur principal */}
                            <Grid size={{ xs: 12, md: clientType === 'entreprise' ? 6 : 12 }}>
                                <Typography variant="h6" gutterBottom>
                                    {clientType === 'entreprise' ? 'Contact principal' : 'Informations personnelles'}
                                </Typography>
                                <Grid container spacing={2}>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField required label="Nom" fullWidth value={form.nom} onChange={setF('nom')} />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField required label="Prénom" fullWidth value={form.prenom} onChange={setF('prenom')} />
                                    </Grid>
                                    <Grid size={{ xs: 12 }}>
                                        <TextField required label="Email" type="email" fullWidth value={form.email} onChange={setF('email')} />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField label="Téléphone" fullWidth value={form.phone} onChange={setF('phone')} />
                                    </Grid>
                                    <Grid size={{ xs: 12, sm: 6 }}>
                                        <TextField label="Téléphone mobile" fullWidth value={form.phone_mobile} onChange={setF('phone_mobile')} />
                                    </Grid>
                                </Grid>
                            </Grid>
                        </Grid>

                        {/* Adresse de facturation */}
                        <Box>
                            <Typography variant="h6" gutterBottom>Adresse de facturation</Typography>
                            <AdresseForm
                                value={{ rue: form.rue, ville: form.ville, code_postal: form.code_postal }}
                                onChange={a => setForm(f => ({ ...f, rue: a.rue, ville: a.ville, code_postal: a.code_postal }))}
                                required
                            />
                            <Box sx={{ mt: 2, maxWidth: 240 }}>
                                <PaysSelect value={form.pays} onChange={setF('pays')} />
                            </Box>
                        </Box>
                    </Stack>
                </DialogContent>
                <DialogActions>
                    <Button onClick={handleCloseDialogCreateClient}>Annuler</Button>
                    <Button
                        variant="contained"
                        disabled={!isFormValid() || creating}
                        onClick={handleCreateClient}
                        sx={{ bgcolor: 'primary.main', '&:hover': { bgcolor: '#a20000' } }}
                    >
                        {creating ? <CircularProgress size={20} color="inherit" /> : 'Créer le client'}
                    </Button>
                </DialogActions>
            </Dialog>
        </Box>
    );
}